import { useCallback, useEffect, useState } from 'react';
import { api, type ClosedPeriod, type Company, type FiscalYear, type ReadinessItem } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as written, periodShort } from '../format';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import type { Panel } from '../components/Shell';

/**
 * Zamykanie okresów i rok obrotowy (E7-S6).
 *
 * Zamknięcie miesiąca to czynność księgowej, nie skutek uboczny wysyłki JPK — dlatego robi się
 * je świadomie i widać, kiedy i dlaczego. Miesiąc jest tu **jednostką**, a nie rekordem:
 * dwanaście wierszy tabeli z jednym przyciskiem każdy czytało się gorzej niż siatka kafelków.
 */
export function PeriodsPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [closed, setClosed] = useState<ClosedPeriod[] | null>(null);
  const [years, setYears] = useState<FiscalYear[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);
  const [reopening, setReopening] = useState<{ period: string; reason: string } | null>(null);
  const [newYear, setNewYear] = useState<{ startsOn: string; endsOn: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [periods, fiscal] = await Promise.all([api.closedPeriods(company.id), api.fiscalYears(company.id)]);
      setClosed(periods);
      setYears(fiscal);
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = company.roles.some((role) => role === 'OWNER' || role === 'ADMIN' || role === 'ACCOUNTANT');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Przed zamknięciem pytamy serwer, co zostało niedokończone.
   *
   * Lista kontrolna nie blokuje zamknięcia — bywa, że szkic zostaje świadomie. Ma tylko
   * sprawić, żeby nikt nie zamknął miesiąca, nie wiedząc, że coś w nim wisi.
   */
  /**
   * Zamknięcie roku idzie przez listę kontrolną z serwera.
   *
   * Blokady nie da się przejść: pokazujemy je i kończymy — rok z niezaksięgowaną fakturą albo
   * niedomkniętym zestawieniem obrotów i sald dałby nieprawdziwe sprawozdanie. Uwagi wymagają
   * potwierdzenia, bo bywają uzasadnione (spółka bez środków trwałych nie ma amortyzacji), ale
   * przemilczane dawałyby złudzenie, że wszystko sprawdzono.
   */
  async function closeYear(year: FiscalYear) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const readiness = await api.yearReadiness(company.id, year.id);
      const label = (item: ReadinessItem) =>
        t.periods.readiness.checks[item.check] + (item.count > 0 ? `: ${item.count}` : '');
      const blocking = readiness.items.filter((item) => !item.passed && item.severity === 'blocking');
      if (blocking.length > 0) {
        setError({ code: 'year_not_ready', message: `${t.periods.readiness.blocked} ${blocking.map(label).join(' · ')}` });
        return;
      }

      const warnings = readiness.items.filter((item) => !item.passed && item.severity === 'warning');
      const question = warnings.length > 0
        ? `${t.periods.readiness.warnings}\n${warnings.map(label).join('\n')}\n\n${t.periods.readiness.confirm}`
        : t.periods.confirmCloseYear(year.startsOn, year.endsOn);
      if (!window.confirm(question)) return;

      const closing = await api.closeFiscalYear(company.id, year.id, warnings.length > 0);
      setNotice(
        closing.resultEntryNo === null
          ? t.periods.readiness.noResult
          : t.periods.readiness.result(closing.resultEntryNo, closing.result ?? '0.00'),
      );
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  async function close(period: string) {
    setBusy(true);
    setError(null);
    try {
      const check = await api.closingCheck(company.id, period);
      if (!check.clean && !window.confirm(t.periods.confirmWithDrafts(period, check.draftInvoices))) {
        return;
      }
      if (check.clean && !window.confirm(t.periods.confirmClose(period))) return;
      await api.closePeriod(company.id, period);
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  const closedSet = new Set((closed ?? []).map((item) => item.period));

  /*
   * Kafelki idą **rokiem obrotowym**, a nie dwunastoma miesiącami wstecz od dziś.
   * Przy pierwszym roku dłuższym niż rok — a taki bywa, bo firma zakłada się w listopadzie —
   * te dwa zakresy się rozjeżdżały: nagłówek liczył „0 / 14", a pod spodem stało dwanaście
   * kafelków, z czego część spoza roku i bez czterech ostatnich miesięcy.
   */
  const today = new Date().toISOString().slice(0, 10);
  const shownYear = years.find((year) => year.startsOn <= today && year.endsOn >= today) ?? years[0] ?? null;
  const months = shownYear ? monthsBetween(shownYear.startsOn, shownYear.endsOn) : recentMonths();

  // Miesiąc, który właśnie się rozlicza — poprzedni. Bieżącego i przyszłych nie da się zamknąć,
  // bo jeszcze trwają; serwer odmawia, więc ekran nie pokazuje przycisku
  const thisMonth = today.slice(0, 7);
  const settledMonth = previousMonth();
  const ended = (month: string) => month < thisMonth;
  const openMonths = months.filter((month) => ended(month) && !closedSet.has(month));
  const closableMonths = months.filter(ended);

  if (reopening) {
    return (
      <>
        <div className="hd">
          <div>
            <h3>{t.periods.reopenTitle(periodShort(reopening.period))}</h3>
            <p>{t.periods.reasonHint}</p>
          </div>
        </div>
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.reopenPeriod(company.id, reopening.period, reopening.reason);
              setReopening(null);
            });
          }}
        >
          {/* Powód jest wymagany po stronie serwera: otwarcie rozliczonego miesiąca
              to zdarzenie, które ktoś kiedyś będzie tłumaczył */}
          <div className="field">
            <label htmlFor="reason">{t.periods.reason}</label>
            <input
              id="reason"
              className="inp"
              value={reopening.reason}
              onChange={(event) => setReopening({ ...reopening, reason: event.target.value })}
              required
            />
          </div>
          <div className="bar2">
            <button type="submit" className="btn" disabled={busy || reopening.reason.trim() === ''}>
              {t.periods.reopen}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={() => setReopening(null)}>
                {t.periods.cancel}
              </button>
            </div>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.periods.title}</h3>
          <p>{t.periods.lead}</p>
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {/* Skutek zamknięcia roku: numer zapisu przeksięgowania wyniku i kwota — do zapamiętania, nie do klikania. */}
      {notice && (
        <p className="hint" role="status">
          {notice}
        </p>
      )}

      <div className="panel">
        <header>
          <h4>{t.periods.years}</h4>
          {editable && !newYear && (
            <div className="sp">
              <button type="button" className="btn sec sm" onClick={() => setNewYear(emptyYear(years))}>
                <Icon.Plus />
                {t.periods.addYear}
              </button>
            </div>
          )}
        </header>

        {years.length === 0 && !newYear && (
          <div className="empty">
            <Icon.Calendar />
            <b>{t.periods.noYears}</b>
            <p>{t.periods.yearHint}</p>
          </div>
        )}

        {years.length > 0 && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.periods.from}</th>
                  <th>{t.periods.to}</th>
                  <th className="n">{t.periods.closedMonths}</th>
                  <th>{t.periods.state}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {years.map((year) => {
                  const inYear = monthsBetween(year.startsOn, year.endsOn);
                  const closedInYear = inYear.filter((month) => closedSet.has(month)).length;
                  return (
                    <tr key={year.id}>
                      <td className="doc">{written(year.startsOn, locale)}</td>
                      <td className="doc">{written(year.endsOn, locale)}</td>
                      <td className="n">
                        {closedInYear} / {inYear.length}
                      </td>
                      <td>
                        <span className={year.status === 'CLOSED' ? 'chip st-draft' : 'chip st-ok'}>
                          <i />
                          {year.status === 'CLOSED' ? t.periods.closed : t.periods.open_}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && year.status === 'OPEN' && (
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={busy}
                              onClick={() => void closeYear(year)}
                            >
                              <Icon.Lock />
                              {t.periods.closeYear}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {newYear && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await api.defineFiscalYear(company.id, newYear.startsOn, newYear.endsOn);
                setNewYear(null);
              });
            }}
          >
            <div className="fields">
              <div className="field">
                <label htmlFor="yFrom">{t.periods.from}</label>
                <input
                  id="yFrom"
                  className="inp"
                  type="date"
                  value={newYear.startsOn}
                  onChange={(event) => setNewYear({ ...newYear, startsOn: event.target.value })}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="yTo">{t.periods.to}</label>
                <input
                  id="yTo"
                  className="inp"
                  type="date"
                  value={newYear.endsOn}
                  onChange={(event) => setNewYear({ ...newYear, endsOn: event.target.value })}
                  required
                />
                <span className="hint">{t.periods.yearHint}</span>
              </div>
            </div>
            <div className="bar2">
              <button type="submit" className="btn" disabled={busy}>
                {t.periods.saveYear}
              </button>
              <div className="sp">
                <button type="button" className="btn ghost" onClick={() => setNewYear(null)}>
                  {t.periods.cancel}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      <div className="panel">
        <header>
          <h4>{t.periods.months}</h4>
          <div className="sp">
            <span className="chip st-ok">
              <i />
              {t.periods.open_}
            </span>
            <span className="chip st-draft">
              <i />
              {t.periods.closed}
            </span>
          </div>
        </header>

        <div className="months">
          {months
            .slice()
            .reverse()
            .map((month) => {
              const entry = (closed ?? []).find((item) => item.period === month);
              const isClosed = closedSet.has(month);
              return (
                <div
                  className={`mo${isClosed ? ' zamk' : ''}${month === settledMonth ? ' biez' : ''}`}
                  key={month}
                >
                  <b>{periodShort(month)}</b>
                  <span className="xs muted">
                    {isClosed ? t.periods.closed : ended(month) ? t.periods.open_ : t.periods.stillRunning}
                    {month === settledMonth && !isClosed && ` · ${t.periods.settled}`}
                  </span>
                  {entry?.closedAt && <span className="xs muted">{written(entry.closedAt, locale)}</span>}
                  {editable && (isClosed || ended(month)) && (
                    <button
                      type="button"
                      className="btn ghost sm"
                      disabled={busy}
                      onClick={() => (isClosed ? setReopening({ period: month, reason: '' }) : void close(month))}
                    >
                      {isClosed ? t.periods.reopen : t.periods.close}
                    </button>
                  )}
                </div>
              );
            })}
        </div>

        {/* To ostrzeżenie tłumaczy, po co w ogóle zamyka się okresy */}
        {closableMonths.length > 0 && openMonths.length === closableMonths.length && (
          <div className="alert a-warn">
            <Icon.Warn />
            <div>
              <b>{t.periods.noneClosed}</b>
              <p>{t.periods.noneClosedHint}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/** Poprzedni miesiąc — ten, który właśnie się rozlicza. */
function previousMonth(): string {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Zapas na wypadek firmy bez roku obrotowego: dwanaście ostatnich zakończonych miesięcy. */
function recentMonths(): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let back = 1; back <= 12; back += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - back, 1);
    months.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

/**
 * Miesiące roku obrotowego.
 *
 * Rok bywa krótszy niż dwanaście miesięcy (rok założenia firmy) i bywa dłuższy — pierwszy rok
 * wolno przedłużyć do osiemnastu. Kafelki na ekranie muszą iść dokładnie tym zakresem, bo inaczej
 * licznik „zamkniętych z iluś" liczy co innego niż to, co widać pod spodem.
 */
export function monthsBetween(startsOn: string, endsOn: string): string[] {
  const months: string[] = [];
  const start = new Date(`${startsOn}T00:00:00`);
  const end = new Date(`${endsOn}T00:00:00`);
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

/** Podpowiedź przy dodawaniu roku: kolejny rok kalendarzowy po ostatnim zdefiniowanym. */
function emptyYear(years: FiscalYear[]): { startsOn: string; endsOn: string } {
  const lastYear = years[0] ? Number(years[0].endsOn.slice(0, 4)) + 1 : new Date().getFullYear();
  return { startsOn: `${lastYear}-01-01`, endsOn: `${lastYear}-12-31` };
}
